import { useEffect, type ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { currentOrg, followSessionNow, setCurrentOrg } from "@/shared/api/client";
import { describeError, errorReference, isApiError } from "@/shared/api/errors";
import type { MeDto } from "@/shared/api/types";
import { postLoginDestination } from "@/shared/auth/routing";
import { MeProvider, SessionProvider, toSession, useMeQuery } from "@/shared/auth/session";
import { FailureCard } from "@/shared/components/FailureCard";
import { PageSkeleton } from "@/shared/components/PageSkeleton";
import { ROUTES } from "@/shared/constants/routes";
import { Button } from "@/shared/ui/button";

/**
 * The organisation every request from these screens is pinned to (ADR-0011 addendum 2026-10-04, plan
 * §5.6). Set here, while rendering, so it is in place before any child makes its first request — only
 * when nothing holds it. A `/me` that names ANOTHER organisation than the held pin is never rendered:
 * see `pinnedElsewhere` below.
 */
function pinOrganisation(me: MeDto): void {
  const tenantId = me.tenant?.id ?? null;
  if (currentOrg() === null || tenantId === null) setCurrentOrg(tenantId);
}

/**
 * `/me` names another organisation than this tab is pinned to: another tab switched or moved the
 * browser and this one missed the broadcast. The screens and the pin must always agree, so the tab
 * renders nothing of the new organisation and follows the session (review 2026-10-04) — kept on the old
 * pin, a screen showing B would write into A if the shared cookie went back to A.
 */
function pinnedElsewhere(me: MeDto | undefined): boolean {
  const held = currentOrg();
  const shown = me?.tenant?.id ?? null;
  return me !== undefined && held !== null && shown !== null && held !== shown;
}

/** Loads /me once (the client refreshes an expired access token first) and hands the account to `children`. */
function MeGate({ children }: { children: (me: MeDto) => ReactNode }) {
  const me = useMeQuery();
  const location = useLocation();
  const elsewhere = pinnedElsewhere(me.data);
  useEffect(() => {
    if (elsewhere) followSessionNow();
  }, [elsewhere]);

  if (me.isPending || elsewhere) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <PageSkeleton />
      </div>
    );
  }
  if (me.isError) {
    // Only a 401 goes to sign-in. A renewal that failed for a blip arrives here as that blip (NETWORK,
    // TIMEOUT, RATE_LIMITED, SERVER) with "Reconnecting…" on, never as the 401 (plan §5.2).
    if (isApiError(me.error) && me.error.status === 401) {
      return <Navigate to={ROUTES.login} replace state={{ from: location.pathname + location.search }} />;
    }
    /*
      The api's own reason, not a guess at it. This gate fails for a rate limit, a suspended
      organisation and an unreachable server alike, and "The server did not answer · check your
      connection" — what it used to say for all three — sends the reader to check a connection that
      is fine. 401 never reaches here (it redirects above), so the reason is always one worth showing;
      the reference is the token support asks for, and `error.message` was never that (board `6a`).
    */
    const failure = describeError(me.error);
    // A renewal refused for a reason that is not a 401 is a client bug, not an ended session: Retry, and
    // the way out if it keeps happening (plan §5.2).
    const refused = isApiError(me.error) && me.error.code === "SESSION_RENEWAL_REFUSED";
    return (
      <div className="p-6">
        <FailureCard
          title={failure.title}
          description="Your account could not be loaded."
          onRetry={() => void me.refetch()}
          actions={
            refused ? (
              <Button asChild variant="secondary" size="sm">
                <Link to={ROUTES.login}>Sign in again</Link>
              </Button>
            ) : undefined
          }
          hideHome
          details={failure.detail}
          reference={errorReference(me.error)}
        />
      </div>
    );
  }
  pinOrganisation(me.data);
  return children(me.data);
}

/** Signed in, organisation optional: onboarding (create an organisation, answer invitations). */
export function RequireSignedIn({ children }: { children: ReactNode }) {
  return <MeGate>{(me) => <MeProvider value={me}>{children}</MeProvider>}</MeGate>;
}

/** Signed in AND inside an organisation: everything under the app shell. Anyone else goes to onboarding. */
export function RequireSession({ children }: { children: ReactNode }) {
  return (
    <MeGate>
      {(me) => {
        const session = toSession(me);
        if (!session) return <Navigate to={postLoginDestination(me)} replace />;
        return (
          <MeProvider value={me}>
            <SessionProvider value={session}>{children}</SessionProvider>
          </MeProvider>
        );
      }}
    </MeGate>
  );
}
